import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  allAvailableCategoryAction,
  addNewCategory,
  updateCategory,
  deleteCategory,
} from "../redux/actions/categories";
import { Dialog } from "@headlessui/react";

export default function CategoryList() {
  const dispatch = useDispatch();
  const { allcategories } = useSelector((state) => state.category);

  const [isOpen, setIsOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [newCategory, setNewCategory] = useState({
    title: "",
    description: "",
    image: "",
    sortOrder: 0,
    showInNavbar: false,
  });

  const totalPages = Math.ceil(allcategories?.length / itemsPerPage);
  const paginatedCategories = allcategories
    ?.sort((a, b) => a.sortOrder - b.sortOrder)
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(allAvailableCategoryAction());
    };
    fetchData();
  }, [dispatch]);

  const resetForm = () => {
    setNewCategory({
      title: "",
      description: "",
      image: "",
      sortOrder: 0,
      showInNavbar: false,
    });
    setFormErrors({});
    setIsEditMode(false);
    setEditingCategoryId(null);
  };

  const handleSaveCategory = async () => {
    const { title, description } = newCategory;
    const errors = {};

    if (!title.trim()) errors.title = "Category Name is required";
    if (!description.trim()) errors.description = "Description is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (isEditMode) {
      await dispatch(updateCategory({ id: editingCategoryId, ...newCategory }));
    } else {
      await dispatch(addNewCategory(newCategory));
    }

    await dispatch(allAvailableCategoryAction());
    setIsOpen(false);
    resetForm();
  };

  return (
    <div className="antialiased">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Category List</h2>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add New Category
          </button>
        </div>

        {allcategories.length === 0 ? (
          <p>No categories available.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {paginatedCategories.map((category) => (
              <div
                key={category.id}
                className="relative border p-4 rounded shadow cursor-pointer"
                onClick={() => {
                  setIsEditMode(true);
                  setEditingCategoryId(category.id);
                  setNewCategory({ ...category });
                  setIsOpen(true);
                }}
              >
                {/* Delete Button */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        "Are you sure you want to delete this category?",
                      )
                    ) {
                      await dispatch(deleteCategory(category.id));
                      await dispatch(allAvailableCategoryAction());
                    }
                  }}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-700 text-white text-xs px-2 py-1 rounded"
                  title="Delete Category"
                >
                  Delete
                </button>

                <h3 className="text-xl font-bold">{category.title}</h3>
                <p className="text-sm text-gray-600">{category.description}</p>
                <h3 className="text-sm font-bold">
                  Sort Order: {category.sortOrder}
                </h3>
                <p className="text-sm">
                  Navbar:
                  {category.showInNavbar ? "Visible" : "Hidden"}
                </p>
              </div>
            ))}

            {/* Pagination */}
            {allcategories.length > itemsPerPage && (
              <div className="flex justify-center items-center mt-4 gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-3 py-1 border rounded ${
                      currentPage === index + 1 ? "bg-blue-500 text-white" : ""
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={isOpen}
          onClose={() => {
            setIsOpen(false);
            resetForm();
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30" />
          <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
            <Dialog.Panel className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
              <Dialog.Title className="text-lg font-bold mb-4">
                {isEditMode ? "Update Category" : "Add New Category"}
              </Dialog.Title>

              <div className="space-y-3">
                {/* Category Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Category Name"
                    className="w-full border px-3 py-2 rounded"
                    value={newCategory.title}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        title: e.target.value,
                      })
                    }
                  />
                  {formErrors.title && (
                    <p className="text-red-500 text-sm">{formErrors.title}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Enter Description"
                    className="w-full border px-3 py-2 rounded"
                    value={newCategory.description}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        description: e.target.value,
                      })
                    }
                  />
                  {formErrors.description && (
                    <p className="text-red-500 text-sm">
                      {formErrors.description}
                    </p>
                  )}
                </div>

                <div className="flex-1">
                  <label
                    htmlFor="sortOrder"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Sort Order
                  </label>
                  <input
                    id="sortOrder"
                    type="number"
                    min="0"
                    className="w-full border px-3 py-2 rounded"
                    value={newCategory.sortOrder || ""}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        sortOrder:
                          e.target.value === "" ? 0 : Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <input
                    type="checkbox"
                    checked={newCategory.showInNavbar}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        showInNavbar: e.target.checked,
                      })
                    }
                  />

                  <label className="text-sm font-medium">
                    Show in Main Navbar
                  </label>
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  className="text-gray-600 hover:underline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  className="bg-green-600 text-white px-4 py-2 rounded"
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </div>
  );
}
